import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import LinearProgress from "@mui/material/LinearProgress";
import Pagination from "@mui/material/Pagination";
import Chip from "@mui/material/Chip";

import ClearIcon from "@mui/icons-material/Close";

import { get } from "lib/api";
import MobileTopBar from "mobile/components/MobileTopBar";
import { IosFilterBar, IosList, IosListItem, IosPage } from "mobile/components/IosParity";

const normalize = (d) =>
  d?.attributes ? { id: d.id, documentId: d.documentId || d.id, ...d.attributes } : { ...d, documentId: d.documentId || d.id };

const TYPE_FILTERS = [
  { key: "sales_order", label: "Sales", icon: "receipt_long", accent: "orange" },
  { key: "invoice", label: "Invoices", icon: "request_quote", accent: "green" },
  { key: "all", label: "All", icon: "list_alt" },
];

const STATUS_FILTERS = [
  { key: "all", label: "All", icon: "filter_list" },
  { key: "draft", label: "Draft", icon: "edit_note" },
  { key: "open", label: "Open", icon: "pending_actions", accent: "orange" },
  { key: "closed", label: "Closed", icon: "check_circle", accent: "green" },
  { key: "cancelled", label: "Cancelled", icon: "block", accent: "red" },
];

export default function SalesMobile() {
  const navigate = useNavigate();
  const [typeFilter, setTypeFilter] = useState("sales_order");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 20;

  useEffect(() => setPage(1), [typeFilter, statusFilter, search]);

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ["m-sales", typeFilter, statusFilter, search, page, pageSize],
    queryFn: () =>
      get("/api/sales-orders", {
        sort: "updatedAt:desc",
        pagination: { page, pageSize },
        populate: { customer: true },
        filters: {
          ...(typeFilter !== "all" && { documentType: { $eq: typeFilter } }),
          ...(statusFilter !== "all" && { status: { $eq: statusFilter } }),
          ...(search && {
            $or: [
              { salesOrderNumber: { $containsi: search } },
              { status: { $containsi: search } },
              { trackingNumber: { $containsi: search } },
              { customer: { name: { $containsi: search } } },
            ],
          }),
        },
      }),
    staleTime: 10_000,
  });

  const rows = useMemo(() => (data?.data || []).map(normalize), [data]);
  const pageCount = data?.meta?.pagination?.pageCount || 1;

  return (
    <IosPage>
      <MobileTopBar
        title="Sales"
        subtitle={isFetching ? "Syncing…" : typeFilter === "invoice" ? "Invoices" : typeFilter === "all" ? "All documents" : "Orders"}
        onBack={() => navigate("/m/dashboard")}
        right={
          <Stack direction="row" spacing={0.5}>
            <IconButton aria-label="Order pulls" size="small" onClick={() => navigate("/m/pull-orders")}>
              <Icon fontSize="small">inventory_2</Icon>
            </IconButton>
            <IconButton aria-label="Core returns" size="small" onClick={() => navigate("/m/core-returns")}>
              <Icon fontSize="small">assignment_return</Icon>
            </IconButton>
          </Stack>
        }
      />

      <Box sx={{ mt: 1, mb: 1 }}>
        <TextField
          fullWidth
          placeholder="Search order, customer, tracking…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Icon fontSize="small">search</Icon>
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" aria-label="Clear search" onClick={() => setSearch("")}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        <IosFilterBar label="Documents" value={typeFilter} options={TYPE_FILTERS} onChange={setTypeFilter} />
        <IosFilterBar label="Status" value={statusFilter} options={STATUS_FILTERS} onChange={setStatusFilter} />
      </Box>

      {isLoading ? <LinearProgress sx={{ mb: 1 }} /> : null}
      {error ? (
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          Failed to load sales orders.
        </Typography>
      ) : null}

      <IosList>
        {rows.map((so) => {
          const documentId = so.documentId;
          const custRaw = so?.customer?.data || so?.customer || null;
          const customer = custRaw?.attributes ? { id: custRaw.id, documentId: custRaw.documentId || custRaw.id, ...custRaw.attributes } : custRaw;
          const customerName = customer?.name || "";
          return (
            <IosListItem
              key={documentId || so.id}
              icon="receipt_long"
              title={so.salesOrderNumber || "Sales order"}
              meta={[customerName, so.documentType ? String(so.documentType).replaceAll("_", " ") : null].filter(Boolean).join(" • ") || "Sales"}
              trailing={so.totalWithTax != null ? `$${Number(so.totalWithTax).toFixed(2)}` : so.status || "draft"}
              accent={so.status === "paid" || so.status === "shipped" ? "green" : so.status === "cancelled" ? "red" : "orange"}
              onClick={() => {
                if (documentId) navigate(`/m/sales/${documentId}`);
              }}
            />
          );
        })}
      </IosList>

      {pageCount > 1 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <Pagination size="small" count={pageCount} page={page} onChange={(e, p) => setPage(p)} />
        </Box>
      ) : null}
    </IosPage>
  );
}
